import {TreeNode} from 'primeng/api';

/**
 * Tree utilities
 */
export class TreeUtils {
    /**
     * TrackBy implementation for TreeNode
     * @param node Node to track
     * @returns Tracking identifier
     */
    public static treeNodeTrackByKey<t_Type>(node?: TreeNode<t_Type>) {
        return node?.key ?? undefined;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Deep clone a TreeNode tree (structure only, data is shared)
     * @param tree Source tree to clone
     * @returns Cloned tree
     */
    public static cloneTree<t_Type>(tree: TreeNode<t_Type>[]): TreeNode<t_Type>[] {
        const cloneNode = (node: TreeNode<t_Type>, parent?: TreeNode<t_Type>): TreeNode<t_Type> => {
            const cloned: TreeNode<t_Type> = {
                ...node,
                parent: parent,
                children: []
            };

            if (node.children) {
                cloned.children = node.children.map((child) => cloneNode(child, cloned));
            }

            return cloned;
        };

        return tree.map((node) => cloneNode(node, undefined));
    }

    // ===============================================================
    // ===============================================================

    /**
     * Connvert a flat array of element into a Tree
     * @param flatItems Flat array
     * @param fnGetKey Get key function
     * @param fnGetParentKey Get parent key function
     * @returns Build tree
     */
    public static flatToTreeNodes<t_Type>(
        flatItems: t_Type[],
        fnGetKey: (model: t_Type) => string,
        fnGetParentKey: (model: t_Type) => string | undefined,
        opts?: {
            /**
             * Get label
             * @param item Visited item
             * @returns New label for the visited node
             */
            fnGetLabel?: (item: t_Type) => string;
            /**
             * Decorate function
             * @param node Visited node
             */
            fnDecorate?: (node: TreeNode<t_Type>) => void;
        }
    ): TreeNode<t_Type>[] {
        const nodeByKey: Record<string, TreeNode<t_Type>> = {};

        let allNodes: TreeNode<t_Type>[] = flatItems.map((x): TreeNode<t_Type> => {
            const currentKey = fnGetKey(x);
            const newNode = {
                key: currentKey,
                label: opts?.fnGetLabel?.(x) ?? currentKey,
                data: x,
                parent: undefined,
                children: []
            };

            nodeByKey[currentKey] = newNode;

            return newNode;
        });

        const rootNodes: TreeNode<t_Type>[] = [];
        allNodes.forEach((node) => {
            const parentKey = fnGetParentKey(node.data!);
            if (parentKey && parentKey in nodeByKey) {
                node.parent = nodeByKey[parentKey];
                node.parent.children?.push(node);
            } else if (!parentKey) rootNodes.push(node);
            else throw new Error('Orphan node detected');
        });

        allNodes = allNodes.map((x) => {
            opts?.fnDecorate?.(x);
            return x;
        });

        return rootNodes;
    }
    /**
     * Prune tree element recursivly with a callback
     * @param currentBranch Current branch
     * @param fnShouldPrune Conditional prune lambda
     */
    public static pruneTree<t_Type>(
        currentBranch: TreeNode<t_Type>[],
        fnShouldPrune: (node: TreeNode<t_Type>) => boolean
    ) {
        for (let i = 0; i < currentBranch.length; ++i) {
            if (fnShouldPrune(currentBranch[i])) {
                // Remove the processed item and adjust the loop counter.
                currentBranch.splice(i, 1);
                i--;

                continue;
            }

            if (currentBranch[i].children) TreeUtils.pruneTree(currentBranch[i].children!, fnShouldPrune);
        }
    }
    /**
     * Visit tree element recursivly with a callback
     * @param currentBranch Current branch
     * @param fnVisitor Visitor lambda
     */
    public static visitTree<t_Type>(currentBranch: TreeNode<t_Type>[], fnVisitor: (node: TreeNode<t_Type>) => void) {
        currentBranch.forEach((x) => {
            fnVisitor(x);
            if (x.children) TreeUtils.visitTree(x.children!, fnVisitor);
        });
    }
    /**
     * Sort tree element recursivly with a callback
     * @param currentBranch Current branch
     * @param fnSort Conditional sort lambda
     */
    public static sortTree<t_Type>(
        currentBranch: TreeNode<t_Type>[],
        fnSort: (a: TreeNode<t_Type>, b: TreeNode<t_Type>) => number
    ) {
        currentBranch.sort(fnSort);

        currentBranch.forEach((x) => {
            if (x.children) TreeUtils.sortTree(x.children!, fnSort);
        });
    }

    // ===============================================================
    // ===============================================================

    /**
     * Find a tree node by lookup key
     * @param currentBranch Current branch/root of tree nodes
     * @param lookupKey Lookup key
     * @returns Found node or undefined
     */
    public static findTreeNodeByKey<t_Type>(
        currentBranch: TreeNode<t_Type>[],
        lookupKey: string
    ): TreeNode<t_Type> | undefined {
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < currentBranch.length; ++i) {
            const current = currentBranch[i];

            if (current.key == lookupKey) return current;

            if (current.children) {
                const res = this.findTreeNodeByKey(current.children, lookupKey);
                if (res) return res;
            }
        }

        return undefined;
    }

    /**
     * Index a whole tree by node key, to avoid walking it on every lookup.
     *
     * Useful when reacting to a stream of events targeting individual nodes: repeated
     * {@link findTreeNodeByKey} calls are O(n) each, turning a burst of n events into O(n²)
     * @param currentBranch : the tree
     * @returns Map of node key to node
     */
    public static indexTreeNodesByKey<t_Type>(currentBranch: TreeNode<t_Type>[]): Map<string, TreeNode<t_Type>> {
        const index = new Map<string, TreeNode<t_Type>>();

        const indexBranch = (branch: TreeNode<t_Type>[]) => {
            for (const node of branch) {
                if (node.key) index.set(node.key, node);
                if (node.children?.length) indexBranch(node.children);
            }
        };
        indexBranch(currentBranch);

        return index;
    }

    /**
     * Identify the current active node and disable it and applying it a specific style
     * @param currentBranch : the tree
     * @param lookupKey: the key uuid
     */
    public static disableCurrentTreeNode<t_Type>(currentBranch: TreeNode<t_Type>[], lookupKey: string) {
        const selfTaskNode = TreeUtils.findTreeNodeByKey(currentBranch, lookupKey);
        if (selfTaskNode) {
            selfTaskNode.selectable = false;
            selfTaskNode.style = 'font-weight: bold';
            selfTaskNode.label += ' (Self)';
        }
    }

    // ===============================================================
    // ===============================================================

    /**
     * Update a TreeNode selection state (partial/full)
     * @param current Current top Node
     * @param selectedNodes Array of the selected nodes
     * @param nodeTrackBy Tracking function for TreeNode
     */
    public static updateTreeNodeSelectionState<t_Type>(
        current: TreeNode<t_Type>,
        selectedNodes: TreeNode<t_Type>[],
        nodeTrackBy: (node: TreeNode<t_Type>) => unknown = this.treeNodeTrackByKey
    ) {
        if (!current || !current.children || current.children.length === 0) {
            return;
        }

        let checkedCount = 0;
        let anyChildrenPartiallySelected = false;
        current.children.forEach((currentChildNode) => {
            this.updateTreeNodeSelectionState(currentChildNode, selectedNodes, nodeTrackBy);
            if (selectedNodes.find((x) => nodeTrackBy(x) == nodeTrackBy(currentChildNode))) {
                checkedCount = checkedCount + 1;
            }

            if (currentChildNode.partialSelected) anyChildrenPartiallySelected = true;
        });

        if (checkedCount != current.children.length) {
            const toRemoveIdx = selectedNodes.findIndex((x) => nodeTrackBy(x) == nodeTrackBy(current));
            if (toRemoveIdx !== -1) {
                selectedNodes.splice(toRemoveIdx, 1);
            }
            current.partialSelected = checkedCount != 0 || anyChildrenPartiallySelected;
        } else {
            if (!selectedNodes.find((x) => nodeTrackBy(x) == nodeTrackBy(current))) {
                selectedNodes.push(current);
            }
            current.partialSelected = false;
        }
    }
    /**
     * Snapshot tree nodes expanded state
     * @param currentBranch Current branch/root of tree nodes
     * @param expanded Snapshot
     */
    public static treeNodesSnapshotExpanded<t_Type>(currentBranch: TreeNode<t_Type>[], expanded: string[]) {
        currentBranch.forEach((x) => {
            if (x.expanded && x.key) expanded.push(x.key);

            if (x.children) this.treeNodesSnapshotExpanded(x.children, expanded);
        });
    }
    /**
     * Restore tree nodes expanded snapshot state
     * @param currentBranch Current branch/root of tree nodes
     * @param expanded Snapshot
     * @param keepAlreadyExpanded Keep already expanded tree nodes
     */
    public static treeNodesRestoreExpanded<t_Type>(
        currentBranch: TreeNode<t_Type>[],
        expanded: string[],
        keepAlreadyExpanded: boolean
    ) {
        currentBranch.forEach((x) => {
            if (x.key) x.expanded = expanded.indexOf(x.key) != -1 || (keepAlreadyExpanded && x.expanded == true);

            if (x.children) this.treeNodesRestoreExpanded(x.children, expanded, keepAlreadyExpanded);
        });
    }
    /**
     * Expand tree nodes by keys
     * @param currentBranch Current branch/root of tree nodes
     * @param keys Keys to expands
     * @param keyI Current key index
     * @returns Last found tree node of the key chain
     */
    public static treeNodesExpandByKeys<t_Type>(
        currentBranch: TreeNode<t_Type>[],
        keys: string[],
        keyI = 0
    ): TreeNode<t_Type> | undefined {
        if (keyI >= keys.length) return undefined;

        const found = currentBranch.find((x) => x.key == keys[keyI]);
        if (!found) return undefined;

        found.expanded = true;

        if (found.children) {
            const subRes = this.treeNodesExpandByKeys(found.children, keys, keyI + 1);
            if (subRes) return subRes;
        }

        return found;
    }
    /**
     * Get visible index for a tree node in a tree nodes root
     * @param rootNodes Root
     * @param targetNode Target tree node
     * @returns Visible index
     */
    public static treeNodeGetVisibleIndex<t_Type>(rootNodes: TreeNode<t_Type>[], targetNode: TreeNode<t_Type>): number {
        const stack: TreeNode<t_Type>[] = [...rootNodes].reverse();
        let count = 0;

        while (stack.length > 0) {
            const currentNode = stack.pop()!;
            if (currentNode == targetNode) return count;

            count++;

            if (currentNode.expanded && currentNode.children) {
                for (let i = currentNode.children.length - 1; i >= 0; i--) {
                    stack.push(currentNode.children[i]);
                }
            }
        }

        return count;
    }
}
